import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertTriangle,
  Archive,
  Calendar,
  CheckCircle2,
  Clock3,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Loader2,
  MapPin,
  Mountain,
  Pencil,
  Plus,
  RefreshCcw,
  UploadCloud,
  Upload,
  Download,
  Sparkles,
  Trash2,
  Trophy
} from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog'
import { EmptyState } from '../components/ui/empty-state'
import { Input } from '../components/ui/input'
import { MeasurementInput, MeasurementValue } from '../components/ui/measurement'
import { Textarea } from '../components/ui/textarea'
import { useToast } from '../components/ui/toast'
import { useAuth } from '../hooks/useAuth'
import { Badge, getBadges } from '../lib/api/badges'
import { useMeasurements } from '../hooks/useMeasurements'
import {
  archiveSeasonalChallenge,
  bulkUploadHikeStrongGeoJson,
  ChallengeCatalogResponse,
  createHikeStrongChallenge,
  createSeasonalChallenge,
  deleteHikeStrongChallenge,
  DIFFICULTY_OPTIONS,
  getHikeStrongChallenges,
  getSeasonalChallenges,
  GeoJsonBulkUploadResult,
  HikeStrongChallenge,
  HikeStrongChallengePayload,
  previewSeasonalChallenges,
  publishSeasonalChallenge,
  resetHikeStrongChallenges,
  SeasonalChallenge,
  SeasonalChallengePayload,
  unpublishSeasonalChallenge,
  uploadHikeStrongBadgeImage,
  uploadSeasonalBadgeImage,
  updateHikeStrongChallenge,
  updateSeasonalChallenge
} from '../lib/api/challenges'
import { displayToMetric } from '../lib/measurements'
import { cn } from '../utils/cn'
import { buildHeaderIndex, downloadCsv, makeGetValue, normalizeText, parseCsvText, validateCsvSchema, withCsvSchemaColumns, withCsvSchemaRow } from '../utils/csv'

type TabType = 'hikestrong' | 'seasonal'
type HikeStrongCsvKey = 'Order' | 'Name' | 'Location' | 'DistanceKm' | 'ElevationGainMeters' | 'Difficulty' | 'GeoJson'
type SeasonalChallengeFormState = Omit<SeasonalChallengePayload, 'distanceKm' | 'elevationGainMeters' | 'recommendationOrder'> & {
  distanceKm: string
  elevationGainMeters: string
  recommendationOrder: string
}

const HIKESTRONG_CSV_TYPE = 'HikeStrongChallenges'
const HIKESTRONG_CSV_VERSION = 2

const HIKESTRONG_CSV_HEADER_ALIASES: Record<HikeStrongCsvKey, string[]> = {
  Order: ['order', 'index'],
  Name: ['hikename', 'name'],
  Location: ['location'],
  DistanceKm: ['distancekm', 'distance'],
  ElevationGainMeters: ['elevationgainm', 'elevationgainmeters', 'elevationgain', 'elevation'],
  Difficulty: ['difficulty'],
  GeoJson: ['geojson', 'routegeojson', 'routejson']
}

const HIKESTRONG_CSV_FALLBACK_INDEX: Record<HikeStrongCsvKey, number> = {
  Order: 0,
  Name: 1,
  Location: 2,
  DistanceKm: 3,
  ElevationGainMeters: 4,
  Difficulty: 5,
  GeoJson: 6
}

const initialHikeStrongForm: HikeStrongChallengePayload = {
  slug: null,
  name: '',
  description: null,
  location: null,
  order: null,
  distanceKm: 0,
  elevationGainMeters: 0,
  difficulty: null,
  mapImageUrl: null,
  badgeImageUrl: null,
  badgeId: null,
  isActive: true
}

const initialSeasonalForm: SeasonalChallengeFormState = {
  slug: null,
  name: '',
  summary: null,
  distanceKm: '',
  elevationGainMeters: '',
  difficulty: null,
  startAt: '',
  endAt: '',
  badgeImageUrl: null,
  badgeId: null,
  sponsorLogoUrl: null,
  sponsorText: null,
  isRecommended: false,
  recommendationOrder: '',
  isPublished: false
}


const formatDate = (value?: string | null) => {
  if (!value) return 'N/A'
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(value))
  } catch {
    return value
  }
}

const formatDateForInput = (isoString: string) => {
  if (!isoString) return ''
  try {
    return new Date(isoString).toISOString().split('T')[0]
  } catch {
    return ''
  }
}

const formatLocalDateForInput = (value = new Date()) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const toPreviewAtIsoString = (value: string) => {
  const [year, month, day] = value.split('-').map((part) => Number(part))
  if (!year || !month || !day) {
    return new Date().toISOString()
  }

  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString()
}

const difficultyColors: Record<string, string> = {
  Easy: 'bg-green-100 text-green-800',
  Moderate: 'bg-yellow-100 text-yellow-800',
  Hard: 'bg-orange-100 text-orange-800',
  Expert: 'bg-red-100 text-red-800'
}

const normalizeGeoJsonForCsv = (value?: string | null) => {
  const trimmed = value?.trim()
  if (!trimmed) return ''

  try {
    return JSON.stringify(JSON.parse(trimmed))
  } catch {
    return trimmed.replace(/\r?\n+/g, ' ')
  }
}

const normalizeGeoJsonFromCsv = (value?: string | null) => {
  const trimmed = value?.trim()
  if (!trimmed) return null

  try {
    return JSON.stringify(JSON.parse(trimmed))
  } catch {
    return trimmed
  }
}

export const ChallengesAdminPage = () => {
  const { user } = useAuth()
  const { addToast } = useToast()
  const measurements = useMeasurements()
  const [activeTab, setActiveTab] = useState<TabType>('hikestrong')
  const [badges, setBadges] = useState<Badge[]>([])
  const [badgesLoading, setBadgesLoading] = useState(false)

  // HikeStrong state
  const [hikeStrongChallenges, setHikeStrongChallenges] = useState<HikeStrongChallenge[]>([])
  const [hikeStrongCatalog, setHikeStrongCatalog] = useState<ChallengeCatalogResponse<HikeStrongChallenge> | null>(null)
  const [hsLoading, setHsLoading] = useState(true)
  const [hsSaving, setHsSaving] = useState(false)
  const [hsForm, setHsForm] = useState<HikeStrongChallengePayload>(initialHikeStrongForm)
  const [hsEditingId, setHsEditingId] = useState<string | null>(null)
  const [hsFormError, setHsFormError] = useState<string | null>(null)
  const [hsDialogOpen, setHsDialogOpen] = useState(false)
  const [hsImporting, setHsImporting] = useState(false)
  const [hsDeletingAll, setHsDeletingAll] = useState(false)
  const [hsDeletingId, setHsDeletingId] = useState<string | null>(null)
  const [geoJsonUploading, setGeoJsonUploading] = useState(false)
  const [geoJsonDropActive, setGeoJsonDropActive] = useState(false)
  const [geoJsonResult, setGeoJsonResult] = useState<GeoJsonBulkUploadResult | null>(null)
  const geoJsonBulkInputRef = useRef<HTMLInputElement>(null)
  const [badgeUploading, setBadgeUploading] = useState(false)
  const [badgeDropActive, setBadgeDropActive] = useState(false)
  const badgeInputRef = useRef<HTMLInputElement>(null)

  // Seasonal state
  const [seasonalChallenges, setSeasonalChallenges] = useState<SeasonalChallenge[]>([])
  const [seasonalCatalog, setSeasonalCatalog] = useState<ChallengeCatalogResponse<SeasonalChallenge> | null>(null)
  const [scLoading, setScLoading] = useState(true)
  const [scSaving, setScSaving] = useState(false)
  const [scForm, setScForm] = useState<SeasonalChallengeFormState>(initialSeasonalForm)
  const [scEditingId, setScEditingId] = useState<string | null>(null)
  const [scFormError, setScFormError] = useState<string | null>(null)
  const [scDialogOpen, setScDialogOpen] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [actioningId, setActioningId] = useState<string | null>(null)
  const [seasonalBadgeUploading, setSeasonalBadgeUploading] = useState(false)
  const [seasonalBadgeDropActive, setSeasonalBadgeDropActive] = useState(false)
  const seasonalBadgeInputRef = useRef<HTMLInputElement>(null)
  const [previewDate, setPreviewDate] = useState(formatLocalDateForInput())
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewChallenges, setPreviewChallenges] = useState<SeasonalChallenge[]>([])

  const role = (user?.role ?? 'Guest').toString()
  const isAdmin = role === 'Admin'
  const isEditor = role === 'Admin' || role === 'Editor'
  const catalogIssues = [
    ...(hikeStrongCatalog?.issues ?? []).map((issue) => ({ ...issue, catalog: 'HikeStrong' as const, repairPath: 'hikestrong' })),
    ...(seasonalCatalog?.issues ?? []).map((issue) => ({ ...issue, catalog: 'Seasonal' as const, repairPath: 'seasonal' }))
  ]
  const hasActiveCatalogIssue = catalogIssues.some((issue) => issue.isActive)

  // Load HikeStrong challenges
  const loadHikeStrong = useCallback(async () => {
    setHsLoading(true)
    try {
      const data = await getHikeStrongChallenges()
      setHikeStrongCatalog(data)
      setHikeStrongChallenges([...data.items].sort((a, b) => a.order - b.order))
    } catch (error) {
      console.error('Failed to load HikeStrong challenges', error)
      addToast({ title: 'Error', description: 'Could not load HikeStrong challenges.' })
    } finally {
      setHsLoading(false)
    }
  }, [addToast])

  // Load Seasonal challenges
  const loadSeasonal = useCallback(async () => {
    setScLoading(true)
    try {
      const data = await getSeasonalChallenges(showArchived)
      setSeasonalCatalog(data)
      setSeasonalChallenges([...data.items].sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime()))
    } catch (error) {
      console.error('Failed to load seasonal challenges', error)
      addToast({ title: 'Error', description: 'Could not load seasonal challenges.' })
    } finally {
      setScLoading(false)
    }
  }, [addToast, showArchived])

  const loadSeasonalPreview = useCallback(async (selectedDate = previewDate) => {
    setPreviewLoading(true)
    try {
      const data = await previewSeasonalChallenges(toPreviewAtIsoString(selectedDate))
      setPreviewChallenges(data.challenges)
    } catch (error) {
      console.error('Failed to load seasonal preview', error)
      addToast({ title: 'Error', description: 'Could not load the seasonal preview.' })
    } finally {
      setPreviewLoading(false)
    }
  }, [addToast, previewDate])

  const loadBadges = useCallback(async () => {
    setBadgesLoading(true)
    try {
      const data = await getBadges(false)
      setBadges(data.filter((badge) => Boolean(badge.renderedImageUrl)))
    } catch (error) {
      console.error('Failed to load badges', error)
      addToast({ title: 'Error', description: 'Could not load saved badges.' })
    } finally {
      setBadgesLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    if (!isEditor) {
      setHsLoading(false)
      setScLoading(false)
      return
    }
    void loadHikeStrong()
    void loadSeasonal()
    void loadBadges()
  }, [isEditor, loadBadges, loadHikeStrong, loadSeasonal])

  useEffect(() => {
    if (!isEditor || activeTab !== 'seasonal') {
      return
    }

    void loadSeasonalPreview()
  }, [activeTab, isEditor, loadSeasonalPreview])

  // HikeStrong handlers
  const resetHsForm = useCallback(() => {
    setHsForm(initialHikeStrongForm)
    setHsEditingId(null)
    setHsFormError(null)
    setBadgeDropActive(false)
    setHsDialogOpen(false)
  }, [])

  const handleHsSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const trimmedName = hsForm.name.trim()
      if (!trimmedName) {
        setHsFormError('Name is required.')
        return
      }
      if (hsForm.distanceKm <= 0 || hsForm.elevationGainMeters <= 0) {
        setHsFormError('Distance and elevation must be greater than 0.')
        return
      }

      setHsFormError(null)
      setHsSaving(true)

      try {
        const payload: HikeStrongChallengePayload = {
          ...hsForm,
          name: trimmedName,
          slug: hsForm.slug?.trim() || null,
          description: hsForm.description?.trim() || null,
          location: hsForm.location?.trim() || null,
          badgeId: hsForm.badgeId?.trim() || null
        }

        if (hsEditingId) {
          const updated = await updateHikeStrongChallenge(hsEditingId, payload)
          setHikeStrongChallenges((prev) =>
            prev.map((item) => (item.id === hsEditingId ? updated : item)).sort((a, b) => a.order - b.order)
          )
          addToast({ title: 'Challenge updated', description: 'Changes saved successfully.' })
        } else {
          const created = await createHikeStrongChallenge(payload)
          setHikeStrongChallenges((prev) => [...prev, created].sort((a, b) => a.order - b.order))
          addToast({ title: 'Challenge created', description: 'New HikeStrong challenge added.' })
        }
        resetHsForm()
      } catch (error) {
        console.error('Failed to save challenge', error)
        addToast({ title: 'Save failed', description: 'Could not save the challenge.' })
      } finally {
        setHsSaving(false)
      }
    },
    [hsEditingId, hsForm, addToast, resetHsForm]
  )

  const handleHsEdit = (challenge: HikeStrongChallenge) => {
    setHsEditingId(challenge.id)
    setHsForm({
      slug: challenge.slug,
      name: challenge.name,
      description: challenge.description ?? null,
      location: challenge.location ?? null,
      order: challenge.order,
      distanceKm: challenge.distanceKm,
      elevationGainMeters: challenge.elevationGainMeters,
      difficulty: challenge.difficulty ?? null,
      mapImageUrl: challenge.mapImageUrl ?? null,
      geoJson: challenge.geoJson ?? null,
      badgeImageUrl: challenge.badgeImageUrl ?? null,
      badgeId: challenge.badgeId ?? null,
      isActive: challenge.isActive
    })
    setHsFormError(null)
    setHsDialogOpen(true)
  }

  // Seasonal handlers
  const resetScForm = () => {
    setScForm(initialSeasonalForm)
    setScEditingId(null)
    setScFormError(null)
    setSeasonalBadgeDropActive(false)
    setScDialogOpen(false)
  }

  const handleScSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      const trimmedName = scForm.name.trim()
      const distanceKm = Number(scForm.distanceKm)
      const elevationGainMeters = Number(scForm.elevationGainMeters)

      if (!trimmedName) {
        setScFormError('Name is required.')
        return
      }
      if (!Number.isFinite(distanceKm) || distanceKm <= 0) {
        setScFormError('Distance must be greater than 0.')
        return
      }
      if (!Number.isFinite(elevationGainMeters) || elevationGainMeters < 0) {
        setScFormError('Elevation cannot be negative.')
        return
      }
      if (!scForm.startAt || !scForm.endAt) {
        setScFormError('Start and end dates are required.')
        return
      }
      if (new Date(scForm.startAt) >= new Date(scForm.endAt)) {
        setScFormError('End date must be after start date.')
        return
      }

      const recommendationOrder = scForm.recommendationOrder.trim()
        ? Number(scForm.recommendationOrder)
        : null
      if (
        scForm.isRecommended &&
        recommendationOrder !== null &&
        (!Number.isInteger(recommendationOrder) || recommendationOrder < 1 || recommendationOrder > 6)
      ) {
        setScFormError('Recommendation order must be a whole number from 1 to 6.')
        return
      }

      setScFormError(null)
      setScSaving(true)

      try {
        const payload: SeasonalChallengePayload = {
          name: trimmedName,
          slug: scForm.slug?.trim() || null,
          summary: scForm.summary?.trim() || null,
          distanceKm,
          elevationGainMeters,
          difficulty: scForm.difficulty,
          startAt: new Date(scForm.startAt).toISOString(),
          endAt: new Date(scForm.endAt).toISOString(),
          badgeImageUrl: scForm.badgeImageUrl?.trim() || null,
          badgeId: scForm.badgeId?.trim() || null,
          sponsorLogoUrl: scForm.sponsorLogoUrl?.trim() || null,
          sponsorText: scForm.sponsorText?.trim() || null,
          isRecommended: scForm.isRecommended,
          recommendationOrder,
          isPublished: scForm.isPublished
        }

        if (scEditingId) {
          const updated = await updateSeasonalChallenge(scEditingId, payload)
          setSeasonalChallenges((prev) =>
            prev.map((item) => (item.id === scEditingId ? updated : item))
          )
          addToast({ title: 'Challenge updated', description: 'Changes saved successfully.' })
        } else {
          const created = await createSeasonalChallenge(payload)
          setSeasonalChallenges((prev) => [created, ...prev])
          addToast({ title: 'Challenge created', description: 'New seasonal challenge added.' })
        }
        void loadSeasonalPreview()
        resetScForm()
      } catch (error) {
        console.error('Failed to save challenge', error)
        addToast({ title: 'Save failed', description: 'Could not save the challenge.' })
      } finally {
        setScSaving(false)
      }
    },
    [scEditingId, scForm, addToast, loadSeasonalPreview]
  )

  const handleScEdit = (challenge: SeasonalChallenge) => {
    setScEditingId(challenge.id)
    setScForm({
      slug: challenge.slug,
      name: challenge.name,
      summary: challenge.summary ?? null,
      distanceKm: challenge.distanceKm.toString(),
      elevationGainMeters: challenge.elevationGainMeters.toString(),
      difficulty: challenge.difficulty ?? null,
      startAt: formatDateForInput(challenge.startAt),
      endAt: formatDateForInput(challenge.endAt),
      badgeImageUrl: challenge.badgeImageUrl ?? null,
      badgeId: challenge.badgeId ?? null,
      sponsorLogoUrl: challenge.sponsorLogoUrl ?? null,
      sponsorText: challenge.sponsorText ?? null,
      isRecommended: challenge.isRecommended,
      recommendationOrder: challenge.recommendationOrder?.toString() ?? '',
      isPublished: challenge.isPublished
    })
    setScFormError(null)
    setScDialogOpen(true)
  }

  const handleSeasonalBadgeUpload = useCallback(async (file: File) => {
    if (!isBadgeImageFile(file)) {
      addToast({ title: 'Invalid image', description: 'Drop a PNG, JPG, or WEBP image for the challenge badge.' })
      return
    }

    setSeasonalBadgeUploading(true)
    try {
      const upload = await uploadSeasonalBadgeImage(
        file,
        scForm.slug?.trim() || scForm.name.trim() || undefined
      )
      setScForm((prev) => ({ ...prev, badgeImageUrl: upload.imageUrl, badgeId: null }))
      addToast({ title: 'Badge image uploaded', description: 'Badge image is ready to save with this challenge.' })
    } catch (error) {
      console.error('Seasonal badge image upload failed', error)
      addToast({ title: 'Upload failed', description: 'Could not upload the badge image.' })
    } finally {
      setSeasonalBadgeUploading(false)
      setSeasonalBadgeDropActive(false)
    }
  }, [addToast, scForm.name, scForm.slug])

  const handleSeasonalBadgeDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setSeasonalBadgeDropActive(false)
    if (seasonalBadgeUploading || scSaving) return
    const file = event.dataTransfer.files?.[0]
    if (file) void handleSeasonalBadgeUpload(file)
  }

  const handleSeasonalBadgeDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (!seasonalBadgeUploading && !scSaving) {
      setSeasonalBadgeDropActive(true)
    }
  }

  const handleSeasonalBadgeDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const related = event.relatedTarget as Node | null
    if (related && event.currentTarget.contains(related)) return
    setSeasonalBadgeDropActive(false)
  }

  const handleTogglePublish = async (challenge: SeasonalChallenge) => {
    setActioningId(challenge.id)
    try {
      const updated = challenge.isPublished
        ? await unpublishSeasonalChallenge(challenge.id)
        : await publishSeasonalChallenge(challenge.id)
      setSeasonalChallenges((prev) =>
        prev.map((item) => (item.id === challenge.id ? updated : item))
      )
      addToast({
        title: updated.isPublished ? 'Published' : 'Unpublished',
        description: `Challenge is now ${updated.isPublished ? 'visible' : 'hidden'} to users.`
      })
      void loadSeasonalPreview()
    } catch (error) {
      console.error('Failed to toggle publish', error)
      addToast({ title: 'Error', description: 'Could not update publish status.' })
    } finally {
      setActioningId(null)
    }
  }

  const handleToggleArchive = async (challenge: SeasonalChallenge) => {
    setActioningId(challenge.id)
    try {
      const updated = await archiveSeasonalChallenge(challenge.id, !challenge.isArchived)
      if (updated.isArchived && !showArchived) {
        setSeasonalChallenges((prev) => prev.filter((item) => item.id !== challenge.id))
      } else {
        setSeasonalChallenges((prev) =>
          prev.map((item) => (item.id === challenge.id ? updated : item))
        )
      }
      addToast({
        title: updated.isArchived ? 'Archived' : 'Restored',
        description: `Challenge has been ${updated.isArchived ? 'archived' : 'restored'}.`
      })
      void loadSeasonalPreview()
    } catch (error) {
      console.error('Failed to toggle archive', error)
      addToast({ title: 'Error', description: 'Could not update archive status.' })
    } finally {
      setActioningId(null)
    }
  }

  const sortedHikeStrong = useMemo(
    () => [...hikeStrongChallenges].sort((a, b) => a.order - b.order),
    [hikeStrongChallenges]
  )

  const renderedBadges = useMemo(
    () => badges.filter((badge) => Boolean(badge.renderedImageUrl)),
    [badges]
  )

  const handleHsSavedBadgeChange = (badgeId: string) => {
    const badge = renderedBadges.find((item) => item.id === badgeId)
    setHsForm((prev) => ({
      ...prev,
      badgeId: badge?.id ?? null,
      badgeImageUrl: badge?.renderedImageUrl ?? null
    }))
  }

  const handleScSavedBadgeChange = (badgeId: string) => {
    const badge = renderedBadges.find((item) => item.id === badgeId)
    setScForm((prev) => ({
      ...prev,
      badgeId: badge?.id ?? null,
      badgeImageUrl: badge?.renderedImageUrl ?? null
    }))
  }

  const parseNumber = (value?: string | null) => {
    if (!value) return 0
    const cleaned = value.replace(/,/g, '').trim()
    const n = Number(cleaned)
    return Number.isFinite(n) ? n : 0
  }

  const handleHsImport = async (file: File) => {
    setHsImporting(true)
    try {
      const text = await file.text()
      // Parse the whole document so quoted multi-line fields round-trip.
      const rows = parseCsvText(text)
      if (rows.length <= 1) {
        addToast({ title: 'Import failed', description: 'CSV appears to be empty.' })
        return
      }

      const header = rows[0].map((h) => h.trim())
      const normalizedHeader = header.map(normalizeText)
      const hasHeader =
        normalizedHeader.includes('hikename') ||
        normalizedHeader.includes('name') ||
        normalizedHeader.includes('distancekm') ||
        normalizedHeader.includes('distancemiles') ||
        normalizedHeader.includes('geojson')
      const dataRows = hasHeader ? rows.slice(1) : rows
      const schemaResult = validateCsvSchema(header, dataRows, {
        expectedType: HIKESTRONG_CSV_TYPE,
        currentVersion: HIKESTRONG_CSV_VERSION,
        entityName: 'HikeStrong challenges'
      })
      if (schemaResult.warning) {
        addToast({ title: 'Legacy CSV format', description: schemaResult.warning, duration: 9000 })
      }

      const headerIndex = hasHeader ? buildHeaderIndex(header, HIKESTRONG_CSV_HEADER_ALIASES) : null
      const getValue = makeGetValue(headerIndex, HIKESTRONG_CSV_FALLBACK_INDEX)
      const getExplicitValue = (cols: string[], aliases: string[]) => {
        if (!hasHeader) return ''
        const index = aliases
          .map(normalizeText)
          .map((alias) => normalizedHeader.indexOf(alias))
          .find((candidate) => candidate >= 0)
        return index === undefined ? '' : (cols[index] ?? '').trim()
      }

      const payloads: HikeStrongChallengePayload[] = dataRows
        .map((cols) => {
          const order = getValue(cols, 'Order')
          const name = getValue(cols, 'Name')
          const location = getValue(cols, 'Location')
          const distance = getValue(cols, 'DistanceKm')
          const elevation = getValue(cols, 'ElevationGainMeters')
          const difficulty = getValue(cols, 'Difficulty')
          const geoJson = normalizeGeoJsonFromCsv(getValue(cols, 'GeoJson'))
          const explicitMiles = parseNumber(getExplicitValue(cols, ['DistanceMiles', 'DistanceMi']))
          const explicitFeet = parseNumber(getExplicitValue(cols, ['ElevationGainFeet', 'ElevationFeet']))
          const distanceKm = explicitMiles > 0 ? displayToMetric(explicitMiles, 'distance', 'Imperial') : parseNumber(distance)
          const elevationGainMeters = explicitFeet > 0 ? displayToMetric(explicitFeet, 'elevation', 'Imperial') : parseNumber(elevation)
          const trimmedName = name?.trim()
          if (!trimmedName || distanceKm <= 0 || elevationGainMeters <= 0) return null
          return {
            slug: null,
            name: trimmedName,
            location: location?.trim() || null,
            order: Number(order) || null,
            distanceKm,
            elevationGainMeters,
            difficulty: difficulty?.trim() || null,
            description: null,
            mapImageUrl: null,
            geoJson,
            badgeImageUrl: null,
            badgeId: null,
            isActive: true
          } as HikeStrongChallengePayload
        })
        .filter((p): p is HikeStrongChallengePayload => p !== null)

      let created = 0
      const failed: { name: string; reason: string }[] = []
      for (const payload of payloads) {
        try {
          const res = await createHikeStrongChallenge(payload)
          setHikeStrongChallenges((prev) => [...prev, res])
          created++
        } catch (error) {
          console.error('Failed to import challenge', payload.name, error)
          failed.push({ name: payload.name, reason: 'Create failed (possible duplicate slug/order)' })
        }
      }

      if (created > 0) {
        addToast({
          title: 'Import complete',
          description: `Imported ${created} challenges${failed.length ? `; ${failed.length} failed` : ''}.`
        })
      } else {
        addToast({ title: 'Import failed', description: 'No challenges were imported.' })
      }

      if (failed.length) {
        console.warn('Import failures', failed)
      }

      await loadHikeStrong()
    } catch (error) {
      console.error('Import error', error)
      addToast({ title: 'Import failed', description: 'Could not parse or import the CSV.' })
    } finally {
      setHsImporting(false)
    }
  }

  const handleHsExport = () => {
    const header = ['#', 'Hike Name', 'Location', 'DistanceKm', 'ElevationGainMeters', 'Difficulty', 'GeoJSON']
    const rows = sortedHikeStrong.map((item) => [
      item.order.toString(),
      item.name,
      item.location ?? '',
      item.distanceKm.toString(),
      item.elevationGainMeters.toString(),
      item.difficulty ?? '',
      normalizeGeoJsonForCsv(item.geoJson)
    ])
    downloadCsv(
      withCsvSchemaColumns(header),
      rows.map((row) => withCsvSchemaRow(HIKESTRONG_CSV_TYPE, HIKESTRONG_CSV_VERSION, row)),
      'hikestrong-challenges.csv'
    )
  }

  const handleHsDeleteAll = useCallback(async () => {
    if (!isAdmin) {
      return
    }

    const confirmed = window.confirm(
      'Delete all HikeStrong challenges? This will also clear HikeStrong progress and workout event history. This cannot be undone.'
    )
    if (!confirmed) {
      return
    }

    const doubleConfirmed = window.confirm(
      'Final confirmation: wipe the entire HikeStrong challenge collection and related progress data?'
    )
    if (!doubleConfirmed) {
      return
    }

    setHsDeletingAll(true)
    try {
      const result = await resetHikeStrongChallenges()
      setHikeStrongChallenges([])
      setGeoJsonResult(null)
      resetHsForm()
      addToast({
        title: 'HikeStrong challenges deleted',
        description: `Removed ${result.deletedChallengesCount} challenge${result.deletedChallengesCount === 1 ? '' : 's'}, cleared ${result.deletedProgressCount} progress record${result.deletedProgressCount === 1 ? '' : 's'}, and reset ${result.deletedWorkoutEventCount} workout event log${result.deletedWorkoutEventCount === 1 ? '' : 's'}.`
      })
    } catch (error) {
      console.error('Failed to delete all HikeStrong challenges', error)
      addToast({ title: 'Delete failed', description: 'Could not clear the HikeStrong challenge collection.' })
    } finally {
      setHsDeletingAll(false)
    }
  }, [addToast, isAdmin, resetHsForm])

  const handleHsDelete = useCallback(async (challenge: HikeStrongChallenge) => {
    if (!isAdmin || hsDeletingId) {
      return
    }

    const confirmed = window.confirm(
      `Remove "${challenge.name}" from HikeStrong challenges? Users currently assigned to this challenge will be moved to the next active challenge. This cannot be undone.`
    )
    if (!confirmed) {
      return
    }

    setHsDeletingId(challenge.id)
    try {
      const result = await deleteHikeStrongChallenge(challenge.id)
      setHikeStrongChallenges((prev) => prev.filter((item) => item.id !== challenge.id))
      if (hsEditingId === challenge.id) {
        resetHsForm()
      }
      addToast({
        title: 'Challenge removed',
        description: `Removed ${challenge.name}${result.reassignedProgressCount > 0 ? ` and reassigned ${result.reassignedProgressCount} active progress record${result.reassignedProgressCount === 1 ? '' : 's'}.` : '.'}`
      })
    } catch (error) {
      console.error('Failed to delete HikeStrong challenge', error)
      addToast({ title: 'Delete failed', description: 'Could not remove the HikeStrong challenge.' })
    } finally {
      setHsDeletingId(null)
    }
  }, [addToast, hsDeletingId, hsEditingId, isAdmin, resetHsForm])

  const triggerHsFileInput = () => {
    const input = document.getElementById('hs-import-input') as HTMLInputElement | null
    if (input) {
      input.value = ''
      input.click()
    }
  }

  const isGeoJsonFile = (file: File) => {
    const name = file.name.toLowerCase()
    return name.endsWith('.geojson') || name.endsWith('.json')
  }

  const isBadgeImageFile = (file: File) => {
    const name = file.name.toLowerCase()
    const type = file.type.toLowerCase()
    return (
      name.endsWith('.png') ||
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg') ||
      name.endsWith('.webp') ||
      type === 'image/png' ||
      type === 'image/jpeg' ||
      type === 'image/webp'
    )
  }

  const handleGeoJsonBulkUpload = useCallback(async (files: File[]) => {
    const geoFiles = files.filter(isGeoJsonFile)
    if (geoFiles.length === 0) {
      addToast({ title: 'No GeoJSON files', description: 'Drop .geojson or .json files to upload.' })
      return
    }

    setGeoJsonUploading(true)
    setGeoJsonResult(null)
    try {
      const result = await bulkUploadHikeStrongGeoJson(geoFiles)
      setGeoJsonResult(result)

      if (result.updatedCount > 0) {
        await loadHikeStrong()
      }

      if (result.failures.length > 0) {
        addToast({
          title: 'GeoJSON upload completed with errors',
          description: `${result.updatedCount} matched, ${result.failures.length} failed.`
        })
      } else {
        addToast({
          title: 'GeoJSON upload complete',
          description: `${result.updatedCount} challenge${result.updatedCount === 1 ? '' : 's'} updated with route data.`
        })
      }
    } catch (error) {
      console.error('GeoJSON bulk upload failed', error)
      addToast({ title: 'Upload failed', description: 'Could not upload GeoJSON files.' })
    } finally {
      setGeoJsonUploading(false)
    }
  }, [addToast, loadHikeStrong])

  const handleGeoJsonDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setGeoJsonDropActive(false)
    if (geoJsonUploading) return
    void handleGeoJsonBulkUpload(Array.from(event.dataTransfer.files ?? []))
  }

  const handleGeoJsonDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (!geoJsonUploading) setGeoJsonDropActive(true)
  }

  const handleGeoJsonDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const related = event.relatedTarget as Node | null
    if (related && event.currentTarget.contains(related)) return
    setGeoJsonDropActive(false)
  }

  const handleSingleGeoJsonUpload = useCallback(async (challenge: HikeStrongChallenge, file: File) => {
    if (!isGeoJsonFile(file)) {
      addToast({ title: 'Invalid file', description: 'Select a .geojson or .json file.' })
      return
    }

    setGeoJsonUploading(true)
    try {
      const geoJson = await file.text()
      const payload: HikeStrongChallengePayload = {
        slug: challenge.slug,
        name: challenge.name,
        description: challenge.description ?? null,
        location: challenge.location ?? null,
        order: challenge.order,
        distanceKm: challenge.distanceKm,
        elevationGainMeters: challenge.elevationGainMeters,
        difficulty: challenge.difficulty ?? null,
        mapImageUrl: challenge.mapImageUrl ?? null,
        geoJson,
        badgeImageUrl: challenge.badgeImageUrl ?? null,
        badgeId: challenge.badgeId ?? null,
        isActive: challenge.isActive
      }
      const updated = await updateHikeStrongChallenge(challenge.id, payload)
      setHikeStrongChallenges((prev) =>
        prev.map((c) => (c.id === updated.id ? updated : c)).sort((a, b) => a.order - b.order)
      )
      const metrics = updated.geoJsonMetrics
      const desc = metrics
        ? `${measurements.format(metrics.distanceKm, 'distance')}, ${measurements.format(metrics.elevationGainMeters, 'elevation')} gain`
        : 'Route data saved'
      addToast({ title: 'GeoJSON applied', description: `${updated.name}: ${desc}` })
    } catch (error) {
      console.error('Single GeoJSON upload failed', error)
      addToast({ title: 'Upload failed', description: 'Could not apply GeoJSON to challenge.' })
    } finally {
      setGeoJsonUploading(false)
    }
  }, [addToast, measurements])

  const handleBadgeUpload = useCallback(async (file: File) => {
    if (!isBadgeImageFile(file)) {
      addToast({ title: 'Invalid image', description: 'Drop a PNG, JPG, or WEBP image for the challenge badge.' })
      return
    }

    setBadgeUploading(true)
    try {
      const upload = await uploadHikeStrongBadgeImage(
        file,
        hsForm.slug?.trim() || hsForm.name.trim() || undefined
      )
      setHsForm((prev) => ({ ...prev, badgeImageUrl: upload.imageUrl, badgeId: null }))
      addToast({ title: 'Badge image uploaded', description: 'Badge image is ready to save with this challenge.' })
    } catch (error) {
      console.error('Badge image upload failed', error)
      addToast({ title: 'Upload failed', description: 'Could not upload the badge image.' })
    } finally {
      setBadgeUploading(false)
      setBadgeDropActive(false)
    }
  }, [addToast, hsForm.name, hsForm.slug])

  const handleBadgeDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setBadgeDropActive(false)
    if (badgeUploading || hsSaving) return
    const file = event.dataTransfer.files?.[0]
    if (file) void handleBadgeUpload(file)
  }

  const handleBadgeDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    if (!badgeUploading && !hsSaving) {
      setBadgeDropActive(true)
    }
  }

  const handleBadgeDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const related = event.relatedTarget as Node | null
    if (related && event.currentTarget.contains(related)) return
    setBadgeDropActive(false)
  }

  if (!isEditor) {
    return (
      <Card className="border-dashed border-amber-500/40 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            Limited Access
          </CardTitle>
          <CardDescription>
            Challenges can only be managed by Admin or Editor accounts.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 shadow-xl">
        <CardHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-amber-500/20 p-3 ring-1 ring-amber-500/30">
              <Trophy className="h-6 w-6 text-amber-200" />
            </div>
            <div>
              <CardTitle className="text-lg">Challenge Management</CardTitle>
              <CardDescription className="text-muted-foreground">
                Create and manage HikeStrong progression and seasonal challenges.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-200/80 font-semibold">
            <Mountain className="h-4 w-4" />
            {hikeStrongChallenges.length} HikeStrong
            <Sparkles className="h-4 w-4 ml-2" />
            {seasonalChallenges.length} Seasonal
          </div>
        </CardHeader>
      </Card>

      {catalogIssues.length > 0 && (
        <Card
          role={hasActiveCatalogIssue ? 'alert' : 'status'}
          data-testid="challenge-catalog-integrity-warning"
          className={hasActiveCatalogIssue
            ? 'border-2 border-red-600 bg-red-50 shadow-md'
            : 'border border-amber-400 bg-amber-50'}
        >
          <CardHeader className="space-y-2">
            <CardTitle className={`flex items-center gap-2 ${hasActiveCatalogIssue ? 'text-red-900' : 'text-amber-900'}`}>
              <AlertTriangle className="h-5 w-5" />
              {hasActiveCatalogIssue ? 'Challenge catalog data integrity failure' : 'Archived challenge record needs repair'}
            </CardTitle>
            <CardDescription className={hasActiveCatalogIssue ? 'text-red-800' : 'text-amber-800'}>
              {hasActiveCatalogIssue
                ? 'One or more active records were omitted because they could not be read. Treat the catalogs below as incomplete until every affected record is repaired.'
                : 'Only archived records were unreadable. User catalogs remain available, but the records still need an admin repair.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {catalogIssues.map((issue) => (
              <div key={`${issue.catalog}-${issue.recordId}`} className="rounded-md border border-current/20 bg-white/70 p-3 text-sm">
                <p className="font-semibold">{issue.catalog} record <span className="font-mono">{issue.recordId}</span>{issue.isActive ? ' (active)' : ' (archived)'}</p>
                <p className="mt-1 break-words font-mono text-xs">{issue.error}</p>
                <p className="mt-2 text-xs">
                  Repair path: <span className="font-mono">PUT /api/admin/challenges/{issue.repairPath}/{issue.recordId}/repair</span> with a complete valid challenge payload.
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab('hikestrong')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'hikestrong'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Mountain className="inline-block h-4 w-4 mr-2" />
          HikeStrong Challenges
        </button>
        <button
          onClick={() => setActiveTab('seasonal')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'seasonal'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Calendar className="inline-block h-4 w-4 mr-2" />
          Seasonal Challenges
        </button>
      </div>

      {/* HikeStrong Tab */}
      {activeTab === 'hikestrong' && (
        <div className="space-y-4">
          <Dialog open={hsDialogOpen} onOpenChange={(open) => { if (!open && !badgeUploading) resetHsForm() }}>
            <DialogContent className="w-[min(96vw,640px)] p-6 space-y-4">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Mountain className="h-5 w-5 text-primary" />
                {hsEditingId ? 'Edit Challenge' : 'Add Challenge'}
              </DialogTitle>
              <DialogDescription>
                Sequential challenges users complete in order.
              </DialogDescription>
              <form className="space-y-4" onSubmit={handleHsSubmit}>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="hs-name">Name *</label>
                    <Input
                      id="hs-name"
                      value={hsForm.name}
                      onChange={(e) => setHsForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Half Dome"
                      disabled={hsSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="hs-slug">Slug</label>
                    <Input
                      id="hs-slug"
                      value={hsForm.slug ?? ''}
                      onChange={(e) => setHsForm((prev) => ({ ...prev, slug: e.target.value }))}
                      placeholder="half-dome"
                      disabled={hsSaving}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="hs-location">Location</label>
                  <Input
                    id="hs-location"
                    value={hsForm.location ?? ''}
                    onChange={(e) => setHsForm((prev) => ({ ...prev, location: e.target.value }))}
                    placeholder="Yosemite National Park, CA"
                    disabled={hsSaving}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="hs-description">Description</label>
                  <Textarea
                    id="hs-description"
                    value={hsForm.description ?? ''}
                    onChange={(e) => setHsForm((prev) => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    placeholder="Describe the challenge..."
                    disabled={hsSaving}
                  />
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="hs-distance-km">Distance ({measurements.unitLabel('distance')}) *</label>
                    <MeasurementInput
                      id="hs-distance-km"
                      kind="distance"
                      min={0}
                      valueMetric={hsForm.distanceKm || null}
                      emptyValue={0}
                      onValueMetricChange={(value) => setHsForm((prev) => ({ ...prev, distanceKm: value ?? 0 }))}
                      disabled={hsSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="hs-elevation">Elevation ({measurements.unitLabel('elevation')}) *</label>
                    <MeasurementInput
                      id="hs-elevation"
                      kind="elevation"
                      min={0}
                      valueMetric={hsForm.elevationGainMeters || null}
                      emptyValue={0}
                      onValueMetricChange={(value) => setHsForm((prev) => ({ ...prev, elevationGainMeters: value ?? 0 }))}
                      disabled={hsSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="hs-order">Order</label>
                    <Input
                      id="hs-order"
                      type="number"
                      min="1"
                      value={hsForm.order ?? ''}
                      onChange={(e) => setHsForm((prev) => ({ ...prev, order: parseInt(e.target.value) || null }))}
                      disabled={hsSaving}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="hs-difficulty">Difficulty</label>
                  <select
                    id="hs-difficulty"
                    value={hsForm.difficulty ?? ''}
                    onChange={(e) => setHsForm((prev) => ({ ...prev, difficulty: e.target.value || null }))}
                    disabled={hsSaving}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select difficulty</option>
                    {DIFFICULTY_OPTIONS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="hs-map-url">Map Image URL</label>
                    <Input
                      id="hs-map-url"
                      value={hsForm.mapImageUrl ?? ''}
                      onChange={(e) => setHsForm((prev) => ({ ...prev, mapImageUrl: e.target.value }))}
                      placeholder="https://..."
                      disabled={hsSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">Badge Image</span>
                      {hsForm.badgeImageUrl ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" />
                          Ready
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">PNG, JPG, or WEBP</span>
                      )}
                      </div>
                      <select
                        value={hsForm.badgeId ?? ''}
                        onChange={(e) => handleHsSavedBadgeChange(e.target.value)}
                        disabled={hsSaving || badgeUploading || badgesLoading}
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      >
                        <option value="">{badgesLoading ? 'Loading saved badges...' : 'Custom image or no saved badge'}</option>
                        {renderedBadges.map((badge) => (
                          <option key={badge.id} value={badge.id}>
                            {badge.name} ({badge.category})
                          </option>
                        ))}
                      </select>
                      <input
                        ref={badgeInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) void handleBadgeUpload(file)
                        e.target.value = ''
                      }}
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (!badgeUploading && !hsSaving) {
                          badgeInputRef.current?.click()
                        }
                      }}
                      onKeyDown={(event) => {
                        if ((event.key === 'Enter' || event.key === ' ') && !badgeUploading && !hsSaving) {
                          event.preventDefault()
                          badgeInputRef.current?.click()
                        }
                      }}
                      onDrop={handleBadgeDrop}
                      onDragOver={handleBadgeDragOver}
                      onDragLeave={handleBadgeDragLeave}
                      className={cn(
                        'rounded-md border border-dashed px-3 py-4 text-left transition-colors',
                        badgeDropActive
                          ? 'border-primary bg-primary/5'
                          : 'border-border bg-muted/20',
                        (badgeUploading || hsSaving)
                          ? 'cursor-not-allowed opacity-70'
                          : 'cursor-pointer hover:border-primary/60 hover:bg-primary/5'
                      )}
                    >
                      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                        {badgeUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                        {badgeUploading ? 'Uploading badge image...' : hsForm.badgeImageUrl ? 'Drop or click to replace badge image' : 'Drop or click to upload badge image'}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        The uploaded image will be used for the challenge thumbnail in the mobile app.
                      </p>
                    </div>
                    {hsForm.badgeImageUrl ? (
                      <div className="flex items-start gap-3 rounded-md border border-border bg-muted/10 p-3">
                        <img
                          src={hsForm.badgeImageUrl}
                          alt="Challenge badge preview"
                          className="h-20 w-20 rounded-lg border border-border object-cover"
                        />
                        <div className="min-w-0 flex-1 space-y-2">
                          <p className="truncate text-xs text-muted-foreground">{hsForm.badgeImageUrl}</p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setHsForm((prev) => ({ ...prev, badgeImageUrl: null, badgeId: null }))}
                            disabled={badgeUploading || hsSaving}
                          >
                            Remove image
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No badge image uploaded yet.</p>
                    )}
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={hsForm.isActive}
                    onChange={(e) => setHsForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={hsSaving}
                    className="rounded border-gray-300"
                  />
                  Active (visible to users)
                </label>

                {hsEditingId && (
                  <div className="space-y-2">
                    <span className="text-sm font-medium">Route GeoJSON</span>
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".geojson,.json"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            const editingChallenge = hikeStrongChallenges.find((c) => c.id === hsEditingId)
                            if (file && editingChallenge) void handleSingleGeoJsonUpload(editingChallenge, file)
                            e.target.value = ''
                          }}
                        />
                        <span className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-muted/60 transition-colors">
                          <MapPin className="h-4 w-4" />
                          Upload GeoJSON
                        </span>
                      </label>
                      {hsForm.geoJson ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                          <CheckCircle2 className="h-3 w-3" />
                          Route attached
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">No route data</span>
                      )}
                    </div>
                  </div>
                )}

                {hsFormError && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    {hsFormError}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <Button type="submit" disabled={hsSaving || badgeUploading} className="gap-2">
                    {hsSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {hsEditingId ? 'Save changes' : 'Add challenge'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetHsForm} disabled={hsSaving || badgeUploading}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">HikeStrong Challenges</h2>
              <p className="text-sm text-muted-foreground">{sortedHikeStrong.length} challenges in sequence</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="hs-import-input"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (file) void handleHsImport(file)
                }}
              />
              <Button variant="outline" size="sm" onClick={triggerHsFileInput} disabled={hsImporting || hsSaving || hsDeletingAll}>
                {hsImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                Import CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleHsExport} disabled={hsSaving || hsDeletingAll}>
                <Download className="h-4 w-4 mr-1" />
                Export CSV
              </Button>
              {isAdmin && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => void handleHsDeleteAll()}
                  disabled={hsDeletingAll || hsLoading || hsImporting || hsSaving || geoJsonUploading || sortedHikeStrong.length === 0}
                  className="gap-2"
                >
                  {hsDeletingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Delete All
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={loadHikeStrong} title="Reload" disabled={hsDeletingAll}>
                {hsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
              </Button>
              <Button size="sm" onClick={() => { resetHsForm(); setHsDialogOpen(true) }} className="gap-2" disabled={hsDeletingAll}>
                <Plus className="h-4 w-4" />
                Add Challenge
              </Button>
            </div>
          </div>

          {/* GeoJSON bulk drop zone */}
          <input
            ref={geoJsonBulkInputRef}
            type="file"
            accept=".geojson,.json,application/geo+json,application/json"
            multiple
            className="hidden"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? [])
              if (files.length > 0) void handleGeoJsonBulkUpload(files)
              event.target.value = ''
            }}
          />
          <div
            role="button"
            tabIndex={geoJsonUploading ? -1 : 0}
            onDrop={handleGeoJsonDrop}
            onDragOver={handleGeoJsonDragOver}
            onDragLeave={handleGeoJsonDragLeave}
            onClick={() => {
              if (!geoJsonUploading) geoJsonBulkInputRef.current?.click()
            }}
            onKeyDown={(event) => {
              if (geoJsonUploading) return
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                geoJsonBulkInputRef.current?.click()
              }
            }}
            className={`rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
              geoJsonDropActive
                ? 'border-primary bg-primary/5'
                : 'border-border bg-muted/20'
            } ${geoJsonUploading ? 'cursor-wait opacity-80' : 'cursor-pointer'}`}
          >
            <UploadCloud className="mx-auto mb-2 h-6 w-6 text-primary" />
            <p className="text-sm font-medium text-foreground">
              {geoJsonUploading ? 'Uploading GeoJSON files...' : 'Drop GeoJSON files here to auto-match challenges by slug'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Filename = challenge slug (e.g. <span className="font-mono">half-dome.geojson</span>). Distance is calculated from coordinates; if altitude is missing, the existing challenge elevation is kept.
            </p>
          </div>

          {geoJsonResult && (
            <div className="rounded-md border border-border p-3 space-y-2">
              <p className="text-sm font-medium">
                GeoJSON upload: {geoJsonResult.updatedCount} matched, {geoJsonResult.failures.length} failed
              </p>
              {geoJsonResult.updated.map((item) => (
                <div key={item.challengeId} className="flex items-center gap-2 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span className="font-medium">{item.challengeName}</span>
                  <span className="text-muted-foreground">
                    ({item.sourceFileName} — {measurements.format(item.distanceKm, 'distance')}, {measurements.format(item.elevationGainMeters, 'elevation')})
                  </span>
                  {item.usedExistingElevationGainFallback && (
                    <span className="text-amber-700">
                      used existing challenge elevation
                    </span>
                  )}
                </div>
              ))}
              {geoJsonResult.failures.map((item, i) => (
                <div key={`${item.sourceFileName}-${i}`} className="flex items-center gap-2 text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span className="font-medium">{item.sourceFileName}</span>
                  <span>{item.error}</span>
                </div>
              ))}
            </div>
          )}

          {hsLoading ? (
            <Card className="flex items-center gap-3 px-6 py-5">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading challenges...</p>
            </Card>
          ) : hikeStrongCatalog?.status.allActiveRecordsUnreadable ? (
            <Card className="border-red-300 bg-red-50 px-6 py-5">
              <p className="font-medium text-red-900">HikeStrong challenges are unavailable until the corrupt active records above are repaired.</p>
            </Card>
          ) : sortedHikeStrong.length === 0 ? (
            <EmptyState
              icon={Mountain}
              title="No HikeStrong challenges"
              description="Add the first challenge to start building the progression."
            />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sortedHikeStrong.map((challenge) => (
                <Card
                  key={challenge.id}
                  className={`shadow-sm ${!challenge.isActive ? 'opacity-60' : ''}`}
                >
                  <CardHeader className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                          {challenge.order}
                        </span>
                        <CardTitle className="text-lg">{challenge.name}</CardTitle>
                      </div>
                      {challenge.difficulty && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${difficultyColors[challenge.difficulty] ?? 'bg-gray-100 text-gray-800'}`}>
                          {challenge.difficulty}
                        </span>
                      )}
                    </div>
                    {challenge.location && (
                      <p className="text-sm text-muted-foreground">{challenge.location}</p>
                    )}
                    {challenge.description && (
                      <CardDescription className="text-sm">{challenge.description}</CardDescription>
                    )}
                    <div className="flex gap-4 text-sm text-muted-foreground">
                      <MeasurementValue valueMetric={challenge.distanceKm} kind="distance" />
                      <MeasurementValue valueMetric={challenge.elevationGainMeters} kind="elevation" />
                    </div>
                    {!challenge.isActive && (
                      <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">
                        Inactive
                      </span>
                    )}
                    {challenge.geoJson ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs border border-emerald-200">
                        <MapPin className="h-3 w-3" />
                        GeoJSON
                        {challenge.geoJsonMetrics && (
                          <span className="text-emerald-600 ml-1">
                            {measurements.format(challenge.geoJsonMetrics.distanceKm, 'distance')} / {measurements.format(challenge.geoJsonMetrics.elevationGainMeters, 'elevation')}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-transparent text-amber-600 text-xs border border-amber-200">
                        <MapPin className="h-3 w-3" />
                        No route data
                      </span>
                    )}
                  </CardHeader>
                  <CardContent className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Clock3 className="h-4 w-4" />
                      {formatDate(challenge.createdAt)}
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".geojson,.json"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) void handleSingleGeoJsonUpload(challenge, file)
                            e.target.value = ''
                          }}
                        />
                        <span className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors">
                          <MapPin className="h-4 w-4" />
                          GeoJSON
                        </span>
                      </label>
                      <Button variant="ghost" size="sm" onClick={() => handleHsEdit(challenge)} className="gap-1">
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void handleHsDelete(challenge)}
                          disabled={hsDeletingId === challenge.id}
                          className="gap-1 text-destructive hover:text-destructive"
                        >
                          {hsDeletingId === challenge.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                          Remove
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Seasonal Tab */}
      {activeTab === 'seasonal' && (
        <div className="space-y-4">
          <Dialog open={scDialogOpen} onOpenChange={(open) => { if (!open && !seasonalBadgeUploading) resetScForm() }}>
            <DialogContent className="w-[min(96vw,640px)] p-6 space-y-4">
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Calendar className="h-5 w-5 text-primary" />
                {scEditingId ? 'Edit Challenge' : 'Add Challenge'}
              </DialogTitle>
              <DialogDescription>
                Time-limited challenges with start/end dates.
              </DialogDescription>
              <form className="space-y-4" onSubmit={handleScSubmit}>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="sc-name">Name *</label>
                    <Input
                      id="sc-name"
                      value={scForm.name}
                      onChange={(e) => setScForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Summer Summit Challenge"
                      disabled={scSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="sc-slug">Slug</label>
                    <Input
                      id="sc-slug"
                      value={scForm.slug ?? ''}
                      onChange={(e) => setScForm((prev) => ({ ...prev, slug: e.target.value }))}
                      placeholder="summer-summit-2025"
                      disabled={scSaving}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium" htmlFor="sc-summary">Summary</label>
                  <Textarea
                    id="sc-summary"
                    value={scForm.summary ?? ''}
                    onChange={(e) => setScForm((prev) => ({ ...prev, summary: e.target.value }))}
                    rows={3}
                    placeholder="Brief description of the challenge..."
                    disabled={scSaving}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="sc-start">Start Date *</label>
                    <Input
                      id="sc-start"
                      type="date"
                      value={scForm.startAt}
                      onChange={(e) => setScForm((prev) => ({ ...prev, startAt: e.target.value }))}
                      disabled={scSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="sc-end">End Date *</label>
                    <Input
                      id="sc-end"
                      type="date"
                      value={scForm.endAt}
                      onChange={(e) => setScForm((prev) => ({ ...prev, endAt: e.target.value }))}
                      disabled={scSaving}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="sc-distance-km">Distance ({measurements.unitLabel('distance')})</label>
                    <MeasurementInput
                      id="sc-distance-km"
                      kind="distance"
                      min={0}
                      valueMetric={parseNumber(scForm.distanceKm) || null}
                      onValueMetricChange={(value) => setScForm((prev) => ({ ...prev, distanceKm: value == null ? '' : value.toString() }))}
                      disabled={scSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="sc-elevation">Elevation ({measurements.unitLabel('elevation')})</label>
                    <MeasurementInput
                      id="sc-elevation"
                      kind="elevation"
                      min={0}
                      valueMetric={parseNumber(scForm.elevationGainMeters) || null}
                      onValueMetricChange={(value) => setScForm((prev) => ({ ...prev, elevationGainMeters: value == null ? '' : value.toString() }))}
                      disabled={scSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="sc-difficulty">Difficulty</label>
                    <select
                      id="sc-difficulty"
                      value={scForm.difficulty ?? ''}
                      onChange={(e) => setScForm((prev) => ({ ...prev, difficulty: e.target.value || null }))}
                      disabled={scSaving}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    >
                      <option value="">Select</option>
                      {DIFFICULTY_OPTIONS.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">Badge Image</span>
                      {scForm.badgeImageUrl ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                          <CheckCircle2 className="h-3 w-3" />
                          Ready
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">PNG, JPG, or WEBP</span>
                      )}
                      </div>
                      <select
                        value={scForm.badgeId ?? ''}
                        onChange={(e) => handleScSavedBadgeChange(e.target.value)}
                        disabled={scSaving || seasonalBadgeUploading || badgesLoading}
                        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                      >
                        <option value="">{badgesLoading ? 'Loading saved badges...' : 'Custom image or no saved badge'}</option>
                        {renderedBadges.map((badge) => (
                          <option key={badge.id} value={badge.id}>
                            {badge.name} ({badge.category})
                          </option>
                        ))}
                      </select>
                      <input
                        ref={seasonalBadgeInputRef}
                      data-testid="seasonal-badge-upload-input"
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) void handleSeasonalBadgeUpload(file)
                        e.target.value = ''
                      }}
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        if (!seasonalBadgeUploading && !scSaving) {
                          seasonalBadgeInputRef.current?.click()
                        }
                      }}
                      onKeyDown={(event) => {
                        if ((event.key === 'Enter' || event.key === ' ') && !seasonalBadgeUploading && !scSaving) {
                          event.preventDefault()
                          seasonalBadgeInputRef.current?.click()
                        }
                      }}
                      onDrop={handleSeasonalBadgeDrop}
                      onDragOver={handleSeasonalBadgeDragOver}
                      onDragLeave={handleSeasonalBadgeDragLeave}
                      className={cn(
                        'rounded-md border border-dashed px-3 py-4 text-left transition-colors',
                        seasonalBadgeDropActive
                          ? 'border-primary bg-primary/5'
                          : 'border-border bg-muted/20',
                        (seasonalBadgeUploading || scSaving)
                          ? 'cursor-not-allowed opacity-70'
                          : 'cursor-pointer hover:border-primary/60 hover:bg-primary/5'
                      )}
                    >
                      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                        {seasonalBadgeUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                        {seasonalBadgeUploading ? 'Uploading badge image...' : scForm.badgeImageUrl ? 'Drop or click to replace badge image' : 'Drop or click to upload badge image'}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        This image is shown as the seasonal challenge badge in the mobile app.
                      </p>
                    </div>
                    {scForm.badgeImageUrl ? (
                      <div className="flex items-start gap-3 rounded-md border border-border bg-muted/10 p-3">
                        <img
                          data-testid="seasonal-badge-preview-image"
                          src={scForm.badgeImageUrl}
                          alt="Seasonal challenge badge preview"
                          className="h-20 w-20 rounded-lg border border-border object-contain"
                        />
                        <div className="min-w-0 flex-1 space-y-2">
                          <p className="truncate text-xs text-muted-foreground">{scForm.badgeImageUrl}</p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setScForm((prev) => ({ ...prev, badgeImageUrl: null, badgeId: null }))}
                            disabled={seasonalBadgeUploading || scSaving}
                          >
                            Remove image
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">No badge image uploaded yet.</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="sc-sponsor-logo">Sponsor Logo URL</label>
                    <Input
                      id="sc-sponsor-logo"
                      value={scForm.sponsorLogoUrl ?? ''}
                      onChange={(e) => setScForm((prev) => ({ ...prev, sponsorLogoUrl: e.target.value }))}
                      placeholder="https://..."
                      disabled={scSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium" htmlFor="sc-sponsor-text">Sponsor Text</label>
                    <Input
                      id="sc-sponsor-text"
                      value={scForm.sponsorText ?? ''}
                      onChange={(e) => setScForm((prev) => ({ ...prev, sponsorText: e.target.value }))}
                      placeholder="Sponsored by..."
                      disabled={scSaving}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-[minmax(0,1fr)_140px] gap-3 rounded-md border border-border bg-muted/20 p-3">
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={scForm.isRecommended ?? false}
                      onChange={(e) => setScForm((prev) => ({ ...prev, isRecommended: e.target.checked }))}
                      disabled={scSaving}
                      className="mt-0.5 rounded border-gray-300"
                    />
                    <span>
                      <span className="block font-medium">Recommended in Discover</span>
                      <span className="block text-xs text-muted-foreground">
                        Pin up to 6 seasonal challenges in the mobile Discover carousel.
                      </span>
                    </span>
                  </label>
                  <div className="space-y-1">
                    <label className="text-xs font-medium" htmlFor="sc-recommendation-order">Order</label>
                    <Input
                      id="sc-recommendation-order"
                      type="number"
                      min={1}
                      max={6}
                      value={scForm.recommendationOrder}
                      onChange={(e) => setScForm((prev) => ({ ...prev, recommendationOrder: e.target.value }))}
                      placeholder="Auto"
                      disabled={scSaving || !scForm.isRecommended}
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={scForm.isPublished}
                    onChange={(e) => setScForm((prev) => ({ ...prev, isPublished: e.target.checked }))}
                    disabled={scSaving}
                    className="rounded border-gray-300"
                  />
                  Published (visible to users)
                </label>

                {scFormError && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    {scFormError}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-3">
                  <Button type="submit" disabled={scSaving || seasonalBadgeUploading} className="gap-2">
                    {scSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {scEditingId ? 'Save changes' : 'Add challenge'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetScForm} disabled={scSaving || seasonalBadgeUploading}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold">Seasonal Challenges</h2>
              <p className="text-sm text-muted-foreground">{seasonalChallenges.length} challenges</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Show archived
              </label>
              <Button variant="ghost" size="icon" onClick={loadSeasonal} title="Reload">
                {scLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />}
              </Button>
              <Button size="sm" onClick={() => { resetScForm(); setScDialogOpen(true) }} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Challenge
              </Button>
            </div>
          </div>

          <Card>
            <CardHeader className="space-y-2">
              <CardTitle className="text-base">User Preview</CardTitle>
              <CardDescription>
                Check which published seasonal challenges would be visible to a user on a specific date.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium" htmlFor="seasonal-preview-date">Preview Date</label>
                  <Input
                    id="seasonal-preview-date"
                    data-testid="seasonal-preview-date"
                    type="date"
                    value={previewDate}
                    onChange={(e) => setPreviewDate(e.target.value)}
                    disabled={previewLoading}
                    className="w-[180px]"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void loadSeasonalPreview()}
                  disabled={previewLoading}
                  className="gap-2"
                >
                  {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                  Refresh preview
                </Button>
              </div>

              {previewLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading preview...
                </div>
              ) : previewChallenges.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No published seasonal challenges would be visible on that date.
                </p>
              ) : (
                <div className="space-y-3">
                  {previewChallenges.map((challenge) => (
                    <div
                      key={challenge.id}
                      data-testid={`seasonal-preview-row-${challenge.id}`}
                      className="flex items-center gap-3 rounded-md border border-border bg-muted/10 p-3"
                    >
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-background">
                        {challenge.badgeImageUrl ? (
                          <img
                            src={challenge.badgeImageUrl}
                            alt={`${challenge.name} badge`}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <ImageIcon className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{challenge.name}</p>
                          {challenge.isRecommended ? (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                              Recommended{challenge.recommendationOrder ? ` #${challenge.recommendationOrder}` : ''}
                            </span>
                          ) : null}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(challenge.startAt)} - {formatDate(challenge.endAt)}
                        </p>
                        <div className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                          {challenge.distanceKm > 0 ? <MeasurementValue valueMetric={challenge.distanceKm} kind="distance" /> : null}
                          {challenge.elevationGainMeters > 0 ? <MeasurementValue valueMetric={challenge.elevationGainMeters} kind="elevation" /> : null}
                        </div>
                        {challenge.sponsorText ? (
                          <p className="mt-1 text-xs text-muted-foreground">{challenge.sponsorText}</p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {scLoading ? (
            <Card className="flex items-center gap-3 px-6 py-5">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading challenges...</p>
            </Card>
          ) : seasonalCatalog?.status.allActiveRecordsUnreadable ? (
            <Card className="border-red-300 bg-red-50 px-6 py-5">
              <p className="font-medium text-red-900">Seasonal challenges are unavailable until the corrupt active records above are repaired.</p>
            </Card>
          ) : seasonalChallenges.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="No seasonal challenges"
              description="Create a time-limited challenge for users to join."
            />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {seasonalChallenges.map((challenge) => {
                const now = new Date()
                const start = new Date(challenge.startAt)
                const end = new Date(challenge.endAt)
                const isActive = now >= start && now <= end
                const isUpcoming = now < start
                const isEnded = now > end

                return (
                  <Card
                    key={challenge.id}
                    className={`shadow-sm ${challenge.isArchived ? 'opacity-50' : ''}`}
                  >
                    <CardHeader className="space-y-2">
                      {challenge.badgeImageUrl ? (
                        <div className="flex h-24 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/10">
                          <img
                            src={challenge.badgeImageUrl}
                            alt={`${challenge.name} badge`}
                            className="h-full w-full object-contain"
                          />
                        </div>
                      ) : null}
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg">{challenge.name}</CardTitle>
                        <div className="flex gap-1">
                          {challenge.isPublished ? (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Published
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Draft
                            </span>
                          )}
                          {isActive && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Active
                            </span>
                          )}
                          {isUpcoming && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Upcoming
                            </span>
                          )}
                          {isEnded && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Ended
                            </span>
                          )}
                          {challenge.isRecommended && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                              Recommended{challenge.recommendationOrder ? ` #${challenge.recommendationOrder}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {formatDate(challenge.startAt)} - {formatDate(challenge.endAt)}
                      </div>
                      {challenge.summary && (
                        <CardDescription className="text-sm">{challenge.summary}</CardDescription>
                      )}
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        {challenge.distanceKm > 0 && <MeasurementValue valueMetric={challenge.distanceKm} kind="distance" />}
                        {challenge.elevationGainMeters > 0 && <MeasurementValue valueMetric={challenge.elevationGainMeters} kind="elevation" />}
                        {challenge.difficulty && (
                          <span className={`px-2 py-0.5 rounded text-xs ${difficultyColors[challenge.difficulty] ?? ''}`}>
                            {challenge.difficulty}
                          </span>
                        )}
                      </div>
                      {challenge.sponsorText && (
                        <p className="text-xs text-muted-foreground italic">{challenge.sponsorText}</p>
                      )}
                    </CardHeader>
                    <CardContent className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleTogglePublish(challenge)}
                          disabled={actioningId === challenge.id}
                          className="gap-1"
                        >
                          {actioningId === challenge.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : challenge.isPublished ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                          {challenge.isPublished ? 'Unpublish' : 'Publish'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleArchive(challenge)}
                          disabled={actioningId === challenge.id}
                          className="gap-1"
                        >
                          <Archive className="h-4 w-4" />
                          {challenge.isArchived ? 'Restore' : 'Archive'}
                        </Button>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleScEdit(challenge)} className="gap-1">
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
